window.KronkKitDiscounts =
  window.KronkKitDiscounts ||
  (() => {
    const rules = [
      {
        code: 'KR-FK-X9Q4T7M2V8-LP36',
        productIds: [9944028610852, 10057181135140, 10067971146020],
      },
      {
        code: 'KR-FK2-R8M4V7Q2X9-LC53',
        productIds: [9944028610852, 10057181135140, 10067971146020, 10490626801956],
      },
      {
        code: 'KR-SP-T7N3K8W5Q2-HM64',
        productIds: [10055986807076, 10407045726500, 10172834873636],
      },
      {
        code: 'KR-DG-P4V9C2R7X5-BL81',
        productIds: [10236693971236, 10055970521380, 10490616938788],
      },
      {
        code: 'KR-PP-N5R8C2Y7Q4-WZ91',
        productIds: [10197206106404, 10055988609316, 10204120056100],
      },
      {
        code: 'KR-GL-Y6Q3M8T4V7-KP25',
        productIds: [10188391252260, 10057181135140, 10172834873636, 10246267437348],
      },
      {
        code: 'KR-YF-H6V3K9D2T5-QA84',
        productIds: [10170988724516, 10171121598756, 10067971146020],
      },
      {
        code: 'KR-YF2-C9R5W2N8Q4-ZL73',
        productIds: [10170988724516, 10171121598756, 10067971146020, 10246267437348],
      },
    ];

    const normalizeCode = (code) => String(code || '').trim().toLowerCase();

    const cartHasRequiredProducts = (cart, productIds) => {
      if (!Array.isArray(cart?.items) || !Array.isArray(productIds) || !productIds.length) return false;

      const quantitiesByProduct = cart.items.reduce((quantities, item) => {
        const productId = Number(item.product_id);
        const quantity = Number(item.quantity) || 0;
        if (productId && quantity > 0) quantities.set(productId, (quantities.get(productId) || 0) + quantity);
        return quantities;
      }, new Map());

      return productIds.every((productId) => (quantitiesByProduct.get(Number(productId)) || 0) > 0);
    };

    const getActiveKitRule = (cart) => {
      const discountCodes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
      const activeCodes = discountCodes.map((discount) => normalizeCode(discount.code)).filter(Boolean);

      return rules.find((rule) => activeCodes.includes(normalizeCode(rule.code))) || null;
    };

    const fetchCart = async () => {
      const response = await fetch(window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js', {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) throw new Error('Unable to validate kit discount.');

      return response.json();
    };

    const clearInvalidDiscount = async (cart, sections = [], sectionsUrl = window.location.pathname) => {
      let currentCart = cart;

      if (!Array.isArray(currentCart?.items) || !Array.isArray(currentCart?.discount_codes)) {
        currentCart = await fetchCart();
      }

      const activeRule = getActiveKitRule(currentCart);
      if (!activeRule || cartHasRequiredProducts(currentCart, activeRule.productIds)) return null;

      const body = {
        discount: '',
      };

      if (Array.isArray(sections) && sections.length) {
        body.sections = sections;
        body.sections_url = sectionsUrl;
      }

      const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) return null;

      try {
        return await response.json();
      } catch (error) {
        return null;
      }
    };

    return {
      rules,
      cartHasRequiredProducts,
      clearInvalidDiscount,
      fetchCart,
      getActiveKitRule,
    };
  })();

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    if (window.KronkKitDiscounts?.clearInvalidDiscount) {
      try {
        const cleanedState = await window.KronkKitDiscounts.clearInvalidDiscount(
          parsedState,
          this.getSectionsToRender().map((section) => section.id),
          window.location.pathname
        );

        if (cleanedState?.sections) parsedState.sections = cleanedState.sections;
      } catch (error) {
        console.warn(error);
      }
    }

    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const hasCartItems = this.querySelectorAll('.cart-item').length > 0;
    this.classList.toggle('is-empty', !hasCartItems);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', !hasCartItems);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
